// src/utils/datetimeUtil.js

export const getFormattedDateTime = (inputDate) => {
  // Adjust to Hong Kong Time (UTC+8)
  inputDate.setHours(inputDate.getHours() + 8);

  // Get individual date and time components
  const year = inputDate.getFullYear().toString();
  const month = (inputDate.getMonth() + 1).toString().padStart(2, "0"); // Month starts from 0
  const day = inputDate.getDate().toString().padStart(2, "0");
  const hours = inputDate.getHours().toString().padStart(2, "0");
  const minutes = inputDate.getMinutes().toString().padStart(2, "0");
  const seconds = inputDate.getSeconds().toString().padStart(2, "0");

  // Format as yyyymmddHHMMSS
  const yyyymmddHHMMSS = `${year}${month}${day}${hours}${minutes}${seconds}`;

  // Format as yyyy-mm-dd
  const yyyy_mm_dd = `${year}-${month}-${day}`;

  // Format as yyyy-mm-dd HH:MM:SS
  const yyyy_mm_dd_HH_MM_SS = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

  return { yyyymmddHHMMSS, yyyy_mm_dd_HH_MM_SS, yyyy_mm_dd };
};

export const getHongKongDateStr = (inputDate = null) => {
  if (!inputDate) {
    inputDate = new Date();
  }
  const { yyyy_mm_dd } = getFormattedDateTime(inputDate);
  return yyyy_mm_dd;
};

export const getHongKongTimeStr = (inputDate = null) => {
  if (!inputDate) {
    inputDate = new Date();
  }
  const { yyyy_mm_dd_HH_MM_SS } = getFormattedDateTime(inputDate);
  return yyyy_mm_dd_HH_MM_SS;
};

export const get_yyyymmddHHMMSS = (inputDate = null) => {
  if (!inputDate) {
    inputDate = new Date();
  }
  const { yyyymmddHHMMSS } = getFormattedDateTime(inputDate);
  return yyyymmddHHMMSS;
};

/**
 * Converts a date string to Hong Kong Time (UTC+8)
 * @param {string} datetimeString - Date string in format "YYYY-MM-DD HH:MM:SS"
 * @returns {Date} Date object in HKT
 */
export const parseHKTDatetimeStr = (datetimeString) => {
  if (!datetimeString) return null;

  // Create date object from string (assumes input is in HKT)
  const [datePart, timePart] = datetimeString.split(" ");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second] = timePart.split(":").map(Number);

  // Create date in HKT
  const date = new Date(year, month - 1, day, hour, minute, second);
  return date;
};

/**
 * Formats a date string into standardized date and time components
 * @param {Date} date - Date object to format
 * @param {string} language - Current language for localization
 * @returns {{date: string, time: string, full: string, auto: string}} Formatted date, time, full datetime and auto (smart format)
 */
export const formatDateTime = (date, language = "en") => {
  // Month names for English
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Get hours in 12-hour format
  const hours = date.getHours();
  const hours12 = hours % 12 || 12;
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const ampm = hours < 12 ? "a.m." : "p.m.";
  const ampmChinese = hours < 12 ? "上午" : "下午";

  // Format time parts with leading zeros
  const minutesStr = minutes.toString().padStart(2, "0");

  // Check if time is midnight (00:00:00)
  const isMidnight = hours === 0 && minutes === 0 && seconds === 0;

  if (language === "en") {
    // Format: 29 May 2025 11:59 p.m. (no seconds)
    const timeStr = `${hours12}:${minutesStr} ${ampm}`;
    const dateStr = `${date.getDate()} ${
      months[date.getMonth()]
    } ${date.getFullYear()}`;
    const fullStr = `${dateStr} ${timeStr}`;

    return {
      date: dateStr,
      time: timeStr,
      full: fullStr,
      auto: isMidnight ? dateStr : fullStr,
    };
  } else {
    // Format: 2025年5月30日下午11時59分 (no seconds)
    const timeStr = `${ampmChinese}${hours12}時${minutesStr}分`;
    const dateStr = `${date.getFullYear()}年${
      date.getMonth() + 1
    }月${date.getDate()}日`;
    const fullStr = `${dateStr}${timeStr}`;

    return {
      date: dateStr,
      time: timeStr,
      full: fullStr,
      auto: isMidnight ? dateStr : fullStr,
    };
  }
};

/**
 * Returns a Date object set to 00:00:00 of the current day
 * @returns {Date} Date object set to midnight (00:00:00) of the current day
 */
export const today0000_hkt = () => {
  const date = new Date();
  // Adjust to HKT by subtracting 8 hours before setting to midnight
  date.setHours(0 - 8, 0, 0, 0);
  return date;
};

/**
 * Returns a Date object set to one year later at 23:59:59
 * @param {Date} [date] - Optional input date. If not provided, uses current date
 * @returns {Date} Date object set to one year later at 23:59:59
 */
export const oneYearLater2359_hkt = (date = new Date()) => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + 1);
  // Adjust to HKT by subtracting 8 hours before setting to 23:59:59
  result.setHours(23 - 8, 59, 59, 0);
  return result;
};
